import { computed, effect, inject, InjectionToken } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { uniqueBy } from '@lassi/util';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { TileLayer } from 'leaflet';
import 'proj4leaflet';
import { DocStatusItem } from './feature.model';
import { FeatureService } from './feature.service';
import { MapService } from './map';
import { createOverlayLayer } from './map/map.model';
import { isIOS } from './platform';

export type DocStatusType = 'viewed' | 'downloaded' | 'failed';

function docStatusKey(type: DocStatusType) {
  return `lassi.doc.${type}`;
}

const delimiter = '|';

function loadDocStatus(type: DocStatusType, storage: Storage): DocStatusItem[] {
  const stored = storage.getItem(docStatusKey(type)) ?? '';

  return stored
    .split(delimiter)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => JSON.parse(item));
}

function saveDocStatus(
  type: DocStatusType,
  storage: Storage,
  state: DocStatusItem[],
) {
  storage.setItem(
    docStatusKey(type),
    state.map((item) => JSON.stringify(item)).join(delimiter),
  );
}

export const DOC_STATUS_STORAGE = new InjectionToken<Storage>(
  'DOC_STATUS_STORAGE',
  {
    factory: () => window.sessionStorage,
  },
);

export const DocStatusStore = signalStore(
  {
    providedIn: 'root',
  },
  withProps(() => ({
    storage: inject(DOC_STATUS_STORAGE),
    map: inject(MapService),
    feature: inject(FeatureService),
  })),
  withState((storage = inject(DOC_STATUS_STORAGE)) => {
    return {
      _viewed: loadDocStatus('viewed', storage),
      _downloaded: loadDocStatus('downloaded', storage),
      _failed: loadDocStatus('failed', storage),
    };
  }),
  withComputed(({ _viewed, _downloaded, _failed }) => ({
    viewed: computed(() => new Set(_viewed().map(({ id }) => id))),
    downloaded: computed(() => new Set(_downloaded().map(({ id }) => id))),
    failed: computed(() => new Set(_failed().map(({ id }) => id))),
    tick: computed(() => uniqueBy([..._viewed(), ..._downloaded()], 'id')),
  })),
  withMethods((store) => {
    return {
      viewDocuments: (items: DocStatusItem[]) => {
        patchState(store, ({ _viewed }) => ({
          _viewed: [..._viewed, ...items],
        }));
      },
      downloadDocuments: (items: DocStatusItem[]) => {
        const itemIds = new Set(items.map((item) => item.id));
        patchState(store, ({ _downloaded, _failed }) => ({
          _downloaded: [..._downloaded, ...items],
          _failed: _failed.filter((item) => !itemIds.has(item.id)),
        }));
      },
      markFailedDownloadDocuments: (items: DocStatusItem[]) => {
        const itemIds = new Set(items.map((item) => item.id));
        patchState(store, ({ _failed, _downloaded }) => ({
          _failed: [..._failed, ...items],
          _downloaded: _downloaded.filter((item) => !itemIds.has(item.id)),
        }));
      },
    };
  }),
  withHooks(
    ({ _viewed, _downloaded, _failed, storage, map, tick, feature }) => {
      const activeLayers = toSignal(feature.activeDocStatusLayers$);

      const layersMap: Record<
        string,
        { layer: TileLayer.WMS; cql_filter?: string }
      > = {};
      function presentTickLayer(
        items: DocStatusItem[],
        property: 'mark_id' | 'annotation_text',
        layerList: string[],
      ) {
        for (const layerName of layerList) {
          let tickLayer = layersMap[layerName]?.layer;
          if (!tickLayer) {
            tickLayer = createOverlayLayer('', false, isIOS(), 999);
            layersMap[layerName] = { layer: tickLayer };
          }

          if (items.length === 0) {
            map.docStatusLayer.removeLayer(tickLayer);
            delete layersMap[layerName];
            continue;
          }

          const ids = items.map((id) => `'${id.id}'`).join(',');
          const cql_filter = `${property} in (${ids})`;

          if (layersMap[layerName].cql_filter === cql_filter) {
            continue;
          }

          layersMap[layerName].cql_filter = cql_filter;
          tickLayer.setParams({
            cql_filter,
            layers: layerName,
            id: layerName,
            styles: property === 'mark_id' ? 'POINT_TICK' : 'POINT_TICK_BUA',
          } as any);

          if (!map.docStatusLayer.hasLayer(tickLayer)) {
            tickLayer.addTo(map.docStatusLayer);
          }
        }
      }

      return {
        onDestroy: () => {
          for (const layer of Object.keys(layersMap)) {
            map.docStatusLayer.removeLayer(layersMap[layer].layer);
            delete layersMap[layer];
          }
        },
        onInit: () => {
          effect(() => {
            const [surveyLabelLayers = '', surveyMarkLayers = ''] =
              activeLayers() ?? [];

            const surveyLabelLayerList = surveyLabelLayers
              .split(',')
              .filter(Boolean);
            const surveyMarkLayerList = surveyMarkLayers
              .split(',')
              .filter(Boolean);

            presentTickLayer(
              tick().filter(({ type }) => type === 'surveyLabel'),
              'annotation_text',
              surveyLabelLayerList,
            );

            presentTickLayer(
              tick().filter(({ type }) => type === 'surveyMark'),
              'mark_id',
              surveyMarkLayerList,
            );

            for (const layer of Object.keys(layersMap)) {
              if (
                surveyLabelLayerList.includes(layer) ||
                surveyMarkLayerList.includes(layer)
              ) {
                continue;
              }
              map.docStatusLayer.removeLayer(layersMap[layer].layer);
              delete layersMap[layer];
            }
          });

          effect(() => {
            saveDocStatus('viewed', storage, _viewed());
          });

          effect(() => {
            saveDocStatus('downloaded', storage, _downloaded());
          });

          effect(() => {
            saveDocStatus('failed', storage, _failed());
          });
        },
      };
    },
  ),
);
